import config from "../../config";
import { BRANDS, type ServiceKey } from "./pairGuideBrands";
import { ServicePairFlowVisual } from "./visuals/ServicePairFlowVisual";

type Props = {
    sourceKey: ServiceKey;
    destKey: ServiceKey;
};

export function PairSyncGuideContent({ sourceKey, destKey }: Props) {
    const source = BRANDS[sourceKey];
    const dest = BRANDS[destKey];
    const same = sourceKey === destKey;
    const stepAccent = dest.spanClass;

    return (
        <div className="bg-white dark:bg-zinc-950 text-gray-900 dark:text-white">
            <div className="max-w-screen-xl pt-20 px-4 py-8 mx-auto lg:py-24 lg:px-6 lg:pt-28">
                <section className="bg-white dark:bg-zinc-950">
                    <div className="max-w-screen-md mx-auto mb-8 text-center lg:mb-12">
                        <h1 className="mb-4 text-4xl font-extrabold tracking-tight text-gray-900 dark:text-white lg:text-5xl">
                            Sync from <span className={source.spanClass}>{source.name}</span> to{" "}
                            <span className={dest.spanClass}>{dest.name}</span>
                        </h1>
                        <p className="mb-8 font-light text-gray-500 sm:text-xl dark:text-gray-400">
                            {same
                                ? `Keep ${source.name} playlists aligned between accounts after you connect them in Music Porter.`
                                : `After you transfer from ${source.name} to ${dest.name}, sync keeps both sides updated automatically.`}
                        </p>
                        <ServicePairFlowVisual sourceKey={sourceKey} destKey={destKey} mode="sync" />
                    </div>
                </section>

                <section className="bg-gray-50 dark:bg-zinc-900">
                    <div className="max-w-screen-xl px-4 py-16 mx-auto lg:py-24 lg:px-6">
                        <div className="max-w-screen-md mx-auto text-center mb-12">
                            <h2 className="mb-4 text-3xl font-extrabold tracking-tight text-gray-900 dark:text-white">
                                How sync works for {source.name} and {dest.name}
                            </h2>
                            <p className="text-gray-500 dark:text-gray-400">
                                Sync links a source playlist with a destination copy so adds and removes flow service-to-service.
                            </p>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                            <div className="text-center">
                                <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-6 text-white font-bold text-xl">
                                    1
                                </div>
                                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Transfer first</h3>
                                <p className="text-gray-500 dark:text-gray-400">
                                    Run a one-time transfer from {source.name} to {dest.name} so the destination playlist exists to pair.
                                </p>
                            </div>
                            <div className="text-center">
                                <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-6 text-white font-bold text-xl">
                                    2
                                </div>
                                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Create sync</h3>
                                <p className="text-gray-500 dark:text-gray-400">
                                    From transfer history in Music Porter, choose Create sync to link the source and destination playlists.
                                </p>
                            </div>
                            <div className="text-center">
                                <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-6 text-white font-bold text-xl">
                                    3
                                </div>
                                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Monitor runs</h3>
                                <p className="text-gray-500 dark:text-gray-400">
                                    Open the sync dashboard to view scheduled runs, success rates, and any tracks that need attention.
                                </p>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="bg-white dark:bg-zinc-950 text-gray-900 dark:text-white mb-10 mt-10">
                    <div className="max-w-screen-xl px-4 py-8 mx-auto lg:py-16 lg:px-6">
                        <div className="max-w-screen-sm mx-auto text-center">
                            <h2 className="mb-4 text-3xl font-extrabold leading-tight tracking-tight text-gray-900 dark:text-white">
                                Ready to set up sync?
                            </h2>
                            <p className="mb-6 font-light text-gray-500 dark:text-gray-400 md:text-lg">
                                Sign in, connect accounts, and enable sync from your latest transfer.
                            </p>
                            <div className="space-y-4 sm:flex sm:space-y-0 sm:space-x-4 justify-center">
                                <a
                                    href={config.signupPath}
                                    className="inline-flex items-center justify-center w-full px-5 py-3 text-sm font-medium text-center text-white bg-purple-700 border border-transparent rounded-lg sm:w-auto hover:bg-purple-800 focus:ring-4 focus:ring-purple-300 dark:bg-purple-600 dark:hover:bg-purple-700 dark:focus:ring-purple-800"
                                >
                                    Sign up for free
                                </a>
                                <a
                                    href="/sync-playlists"
                                    className="text-purple-700 bg-white border border-purple-700 hover:bg-purple-50 focus:ring-4 focus:ring-purple-300 font-medium rounded-lg text-sm px-8 py-3 dark:text-purple-400 dark:border-purple-400 dark:hover:bg-purple-900/20 focus:outline-none dark:focus:ring-purple-800 transition-colors duration-200"
                                >
                                    Sync overview
                                </a>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="bg-white dark:bg-zinc-950 py-12">
                    <div className="max-w-screen-xl px-4 mx-auto lg:px-6">
                        <div className="max-w-screen-md mx-auto text-center mb-12">
                            <h2 className="mb-4 text-3xl font-extrabold tracking-tight text-gray-900 dark:text-white">
                                Step-by-step: enable {source.name} → {dest.name} sync
                            </h2>
                        </div>
                        <div className="max-w-4xl mx-auto">
                            <div className="bg-white dark:bg-zinc-950 rounded-2xl border border-zinc-200 dark:border-zinc-800 shadow-sm p-8">
                                <div className="space-y-8">
                                    <div className="flex items-start gap-4">
                                        <div
                                            className={`text-2xl font-bold ${stepAccent} bg-gray-100 dark:bg-zinc-800 rounded-full w-12 h-12 flex items-center justify-center`}
                                        >
                                            1
                                        </div>
                                        <div>
                                            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Complete a transfer</h3>
                                            <p className="text-lg text-gray-800 dark:text-gray-300 leading-relaxed">
                                                Finish a successful transfer from {source.name} to {dest.name} so both playlists exist.
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-start gap-4">
                                        <div
                                            className={`text-2xl font-bold ${stepAccent} bg-gray-100 dark:bg-zinc-800 rounded-full w-12 h-12 flex items-center justify-center`}
                                        >
                                            2
                                        </div>
                                        <div>
                                            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Open transfer history</h3>
                                            <p className="text-lg text-gray-800 dark:text-gray-300 leading-relaxed">
                                                In Music Porter, go to transfer history and locate the completed {source.name} → {dest.name}{" "}
                                                job.
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-start gap-4">
                                        <div
                                            className={`text-2xl font-bold ${stepAccent} bg-gray-100 dark:bg-zinc-800 rounded-full w-12 h-12 flex items-center justify-center`}
                                        >
                                            3
                                        </div>
                                        <div>
                                            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Create sync</h3>
                                            <p className="text-lg text-gray-800 dark:text-gray-300 leading-relaxed">
                                                Use Create sync to link the playlists. Music Porter will keep {dest.name} aligned with
                                                changes on {source.name}.
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-start gap-4">
                                        <div
                                            className={`text-2xl font-bold ${stepAccent} bg-gray-100 dark:bg-zinc-800 rounded-full w-12 h-12 flex items-center justify-center`}
                                        >
                                            4
                                        </div>
                                        <div>
                                            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Review sync runs</h3>
                                            <p className="text-lg text-gray-800 dark:text-gray-300 leading-relaxed">
                                                Check the sync section in your dashboard for logs, timestamps, and any items that could
                                                not be matched.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </div>

            <section className="bg-gray-50 dark:bg-zinc-800">
                <div className="max-w-screen-xl px-4 py-16 mx-auto lg:py-24 lg:px-6">
                    <div className="max-w-screen-md mx-auto text-center">
                        <h2 className="mb-6 text-3xl font-extrabold leading-tight tracking-tight text-gray-900 dark:text-white lg:text-4xl">
                            Keep {source.name} and {dest.name} in perfect sync
                        </h2>
                        <p className="mb-8 font-light text-gray-500 dark:text-gray-400 md:text-lg">
                            Automate playlist updates after your first transfer—no manual rebuild on each service.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                            <a
                                href={config.signupPath}
                                className="text-white bg-purple-700 hover:bg-purple-800 focus:ring-4 focus:ring-purple-300 font-medium rounded-lg text-sm px-8 py-3 dark:bg-purple-600 dark:hover:bg-purple-700 focus:outline-none dark:focus:ring-purple-800 transition-colors duration-200"
                            >
                                Start Now
                            </a>
                            <a
                                href="/transfer-playlists-and-songs"
                                className="text-purple-700 bg-white border border-purple-700 hover:bg-purple-50 focus:ring-4 focus:ring-purple-300 font-medium rounded-lg text-sm px-8 py-3 dark:text-purple-400 dark:border-purple-400 dark:hover:bg-purple-900/20 focus:outline-none dark:focus:ring-purple-800 transition-colors duration-200"
                            >
                                Learn About Transfer
                            </a>
                        </div>
                        <div className="mt-8 text-sm text-gray-500 dark:text-gray-400">
                            <p>✓ No credit card required • ✓ Free forever transfer • ✓ Lifetime plan available</p>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
